import { ControlGroup, FormActions, GenericReactForm, PasswordInput, StringInput, SubmitButton } from 'client/js/util/form_utils';
import { Link } from 'react-router-dom';
import SixDigitCodeInput from 'client/js/components/six_digit_code_input';
import PropTypes from 'prop-types';
import ReCAPTCHA from 'react-google-recaptcha';

class PasswordResetCaptcha extends React.Component {
  state = { mounted: false }

  componentDidMount() {
    this.setState({ mounted: true });
  }

  change = (value) => {
    this.context.formDispatch('value_changed', null, { name: 'recaptcha_response', value: value });
  }

  render() {
    if (!this.props.show)
      return null;

    return (
      <ControlGroup name="captcha" errors={this.context.serverErrors.captcha}>
        <div className="signup-captcha-text">
          Bitte zeige uns, dass Du ein Mensch bist:
        </div>
        {this.state.mounted && <ReCAPTCHA sitekey={this.props.recaptcha_site_key} onChange={this.change} />}
      </ControlGroup>
    );
  }
}

PasswordResetCaptcha.contextTypes = {
  formDispatch: PropTypes.func,
  serverErrors: PropTypes.object
};

class PasswordReset extends React.Component {
  state = {
    step: this.props.initial_code ? 'password' : 'request',
    username: this.props.initial_username || '',
    code: this.props.initial_code || '',
    email: null,
    codeLoading: false,
    codeError: null,
    usernameLooksLikeAddress: false
  }

  usernameLooksLikeAddress = (username) => {
    const value = (username || '').trim();

    return value.includes('@') || value.includes('.');
  }

  usernameWarning = (formData) => {
    if (!this.usernameLooksLikeAddress(formData.username))
      return null;

    return {
      username: ['Bitte gib Deinen Benutzernamen ein, keine E-Mail-Adresse und keinen Hostnamen.']
    };
  }

  requestDispatch = (action, _originalEvent, data) => {
    if (action === 'form:change' && data.changedField === 'username') {
      this.setState({ usernameLooksLikeAddress: this.usernameLooksLikeAddress(data.formData.username) });
      return;
    }

    if (action !== 'form:success')
      return;

    this.setState({
      step: 'code',
      username: data.object.username,
      email: data.object.email,
      code: '',
      codeError: null
    });
  }

  passwordDispatch = (action, _originalEvent, data) => {
    if (action !== 'form:success')
      return;

    window.location.href = data.object.return_url || '/usercp';
  }

  changeCode = (code) => {
    this.setState({ code: code, codeError: null });
  }

  verifyCode = async (code = this.state.code) => {
    if (code.length !== 6 || this.state.codeLoading)
      return;

    this.setState({ codeLoading: true, codeError: null });

    try {
      const response = await fetch('/lostpassword/code', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
        },
        body: JSON.stringify({ username: this.state.username, code: code })
      });
      const data = await response.json();

      if (response.ok && data.status === 'ok') {
        this.setState({ step: 'password', codeLoading: false });
        return;
      }

      this.setState({
        codeError: data.message || 'Der Code konnte nicht geprüft werden.',
        code: '',
        codeLoading: false
      });
    } catch {
      this.setState({
        codeError: 'Ein Fehler ist aufgetreten. Bitte versuche es erneut.',
        codeLoading: false
      });
    }
  }

  renderRequestStep() {
    const usernameWarning = 'Bitte gib Deinen Benutzernamen ein, keine E-Mail-Adresse und keinen Hostnamen.';

    return (
      <div className="login-form-panel login-form-panel-compact">
        <h1 className="login-title">Passwort vergessen</h1>
        <p className="lead">Gib Deinen Benutzernamen ein. Wir senden Dir einen 6-stelligen Code an die hinterlegte E-Mail-Adresse.</p>

        <GenericReactForm namespace="password_reset_request" url="/lostpassword" dispatch={this.requestDispatch} onValidation={this.usernameWarning}>
          <StringInput required name="username" autoComplete="username" label="Benutzername" />
          {this.state.usernameLooksLikeAddress && (
            <div className="password-reset-username-warning text-danger">
              {usernameWarning}
            </div>
          )}
          <PasswordResetCaptcha show={this.props.show_captcha} recaptcha_site_key={this.props.recaptcha_site_key} />

          <FormActions>
            <SubmitButton text="Code anfordern" />
          </FormActions>
        </GenericReactForm>

        <div className="login-review-prompt">
          <Link to="/login">Zurück zum Login</Link>
        </div>
      </div>
    );
  }

  renderCodeStep() {
    const canVerify = this.state.code.length === 6 && !this.state.codeLoading;

    return (
      <div className="login-form-panel login-form-panel-compact">
        <h1 className="login-title">Code eingeben</h1>
        <p>Wir haben den Code an <strong>{this.state.email}</strong> gesendet.</p>

        {this.state.codeError && (
          <div className="alert alert-danger">{this.state.codeError}</div>
        )}

        <form onSubmit={(e) => this.verifyCode(e.preventDefault() || this.state.code)}>
          <SixDigitCodeInput
            value={this.state.code}
            onChange={this.changeCode}
            onComplete={this.verifyCode}
            disabled={this.state.codeLoading}
          />

          <FormActions>
            <button className="btn btn-lg btn-primary" disabled={!canVerify}>
              Code prüfen
            </button>
          </FormActions>
        </form>
      </div>
    );
  }

  renderPasswordStep() {
    const url = `/lostpassword/password?username=${encodeURIComponent(this.state.username)}&token=${encodeURIComponent(this.state.code)}`;

    return (
      <div className="login-form-panel login-form-panel-compact">
        <h1 className="login-title">Passwort ändern</h1>
        <p className="lead">Lege jetzt Dein neues Passwort fest.</p>

        <GenericReactForm namespace="lost_password" url={url} dispatch={this.passwordDispatch}>
          <PasswordInput required name="password" label="Neues Passwort" />
          <StringInput required name="password_confirmation" type="password" autoComplete="new-password" label="Bestätigung" />

          <FormActions>
            <SubmitButton text="Passwort ändern" />
          </FormActions>
        </GenericReactForm>
      </div>
    );
  }

  render() {
    if (this.state.step === 'password')
      return this.renderPasswordStep();

    if (this.state.step === 'code')
      return this.renderCodeStep();

    return this.renderRequestStep();
  }
}

PasswordReset.defaultProps = {
  initial_code: null,
  initial_username: null,
  show_captcha: false
};

PasswordReset.propTypes = {
  initial_code: PropTypes.string,
  initial_username: PropTypes.string,
  recaptcha_site_key: PropTypes.string,
  show_captcha: PropTypes.bool
};

export default PasswordReset;
